import { History, Plus } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { StatusPill } from "@/components/ui/status-pill";
import { isCustomThematic, thematicClasses } from "@/lib/thematic";
import { cn } from "@/lib/utils/cn";

import { useCatalogueList } from "../api";
import type { CatalogueItem, CatalogueKind } from "../types";
import type { CatalogueDialogs } from "./dialogs";
import { RowActions } from "./RowActions";

export type ListTabKey = "thematics" | "sectors" | "characteristics";

interface Group {
  key: string;
  heading: string | null;
  rows: CatalogueItem[];
  /** Preset for the group's « Ajouter ». */
  defaults?: Record<string, string>;
}

/** One section: heading, its add / recent buttons, and its grouped rows. */
function Section({
  kind,
  title,
  groups,
  movable,
  dialogs,
  extra,
}: {
  kind: CatalogueKind;
  title: string;
  groups: Group[];
  movable: boolean;
  dialogs: CatalogueDialogs;
  extra?: (row: CatalogueItem) => ReactNode;
}) {
  const { t } = useTranslation();
  const empty = groups.every((group) => group.rows.length === 0);
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="mr-auto text-lg font-semibold">{title}</h2>
        <Button variant="outline" onClick={() => dialogs.openRecent(kind)}>
          <History aria-hidden />
          {t("catalogue.recent")}
        </Button>
        <Button onClick={() => dialogs.openAdd(kind)}>
          <Plus aria-hidden />
          {t(`catalogue.addTitle.${kind}`)}
        </Button>
      </div>
      {empty && <p className="text-sm text-muted-foreground">{t("catalogue.empty")}</p>}
      {groups
        .filter((group) => group.rows.length > 0 || group.heading)
        .map((group) => (
          <div key={group.key} className="rounded-md border border-border">
            {group.heading !== null && (
              <div className="flex items-center gap-2 border-b border-border bg-muted/50 px-3 py-2">
                <h3 className="mr-auto text-sm font-semibold">{group.heading}</h3>
                {group.defaults && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => dialogs.openAdd(kind, group.defaults)}
                  >
                    <Plus aria-hidden />
                    {t("catalogue.add")}
                  </Button>
                )}
              </div>
            )}
            <ul className="divide-y divide-border">
              {group.rows.map((row, index) => (
                <li
                  key={row.id}
                  data-catalogue-row={row.id}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2"
                >
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-2 font-medium">
                      {extra?.(row)}
                      {row.name_fr}
                      {!row.name_en.trim() && (
                        <StatusPill tone="warning">{t("catalogue.missingTranslation")}</StatusPill>
                      )}
                    </p>
                    {row.name_en.trim() && (
                      <p className="text-xs text-muted-foreground">
                        <span className="mr-1 rounded bg-muted px-1 font-semibold">EN</span>
                        {row.name_en}
                      </p>
                    )}
                  </div>
                  <RowActions
                    kind={kind}
                    item={row}
                    movable={movable}
                    isFirst={index === 0}
                    isLast={index === group.rows.length - 1}
                    onEdit={() => dialogs.openEdit(kind, row)}
                    onDelete={() => dialogs.openDelete(kind, row)}
                    onHistory={() => dialogs.openHistory(kind, row)}
                  />
                </li>
              ))}
            </ul>
          </div>
        ))}
    </section>
  );
}

/** Rows grouped under their parent, parents in their own list order. */
function byParent(
  rows: CatalogueItem[],
  parents: CatalogueItem[],
  field: string,
  orphanHeading: string,
): Group[] {
  const groups: Group[] = parents.map((parent) => ({
    key: parent.id,
    heading: parent.name,
    rows: rows.filter((row) => row.parent_id === parent.id),
    defaults: { [field]: parent.id },
  }));
  const orphans = rows.filter((row) => !row.parent_id);
  if (orphans.length) {
    groups.push({ key: "none", heading: orphanHeading, rows: orphans });
  }
  return groups;
}

/**
 * The thematic, sector and characteristic lists, with their category / main
 * characteristic lists below — the SPA side of the Django list pages.
 */
export function ListTab({ tab, dialogs }: { tab: ListTabKey; dialogs: CatalogueDialogs }) {
  const { t } = useTranslation();
  const thematics = useCatalogueList("thematic", tab === "thematics");
  const sectors = useCatalogueList("sector", tab === "sectors");
  const categories = useCatalogueList("sector_category", tab === "sectors");
  const characteristics = useCatalogueList("characteristic", tab === "characteristics");
  const groups = useCatalogueList("characteristic_group", tab === "characteristics");

  const queries =
    tab === "thematics"
      ? [thematics]
      : tab === "sectors"
        ? [sectors, categories]
        : [characteristics, groups];
  if (queries.some((query) => query.isPending)) {
    return <p className="p-4 text-sm text-muted-foreground">{t("catalogue.loading")}</p>;
  }
  if (queries.some((query) => query.isError)) {
    return <p role="alert" className="p-4 text-sm text-destructive-ink">{t("catalogue.error")}</p>;
  }

  if (tab === "thematics") {
    return (
      <Section
        kind="thematic"
        title={t("catalogue.tabs.thematics")}
        groups={[{ key: "all", heading: null, rows: thematics.data ?? [] }]}
        movable
        dialogs={dialogs}
        extra={(row) => {
          const theme = thematicClasses(row.css_class);
          return (
            <>
              <span
                aria-hidden
                className={cn("inline-block size-3 rounded-full", theme.solid)}
              />
              <span className="sr-only">
                {isCustomThematic(row.css_class)
                  ? `${t("catalogue.colours.custom")} ${row.colour ?? ""}`
                  : row.css_class
                    ? t(`catalogue.colours.${row.css_class}`)
                    : ""}
              </span>
              <span className="order-last flex flex-wrap gap-1">
                {(["experts_enabled", "tools_enabled", "financial_aids_enabled"] as const)
                  .filter((field) => row[field])
                  .map((field) => (
                    <StatusPill key={field} tone="success">
                      {t(`catalogue.fields.${field}`)}
                    </StatusPill>
                  ))}
              </span>
            </>
          );
        }}
      />
    );
  }

  if (tab === "sectors") {
    return (
      <div className="space-y-8">
        <Section
          kind="sector"
          title={t("catalogue.tabs.sectors")}
          groups={byParent(
            sectors.data ?? [],
            categories.data ?? [],
            "category",
            t("catalogue.noCategory"),
          )}
          movable={false}
          dialogs={dialogs}
        />
        <Section
          kind="sector_category"
          title={t("catalogue.sectorCategories")}
          groups={[{ key: "all", heading: null, rows: categories.data ?? [] }]}
          movable
          dialogs={dialogs}
        />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <Section
        kind="characteristic"
        title={t("catalogue.tabs.characteristics")}
        groups={byParent(
          characteristics.data ?? [],
          groups.data ?? [],
          "group",
          t("catalogue.noGroup"),
        )}
        movable
        dialogs={dialogs}
      />
      <Section
        kind="characteristic_group"
        title={t("catalogue.characteristicGroups")}
        groups={[{ key: "all", heading: null, rows: groups.data ?? [] }]}
        movable
        dialogs={dialogs}
      />
    </div>
  );
}
